import { QueryClient } from "@tanstack/react-query";
import { authManager } from "../data/managers/AuthManager";
import { HttpMethodEnum } from "../data/models/HttpMethodEnum";

export interface ApiResponse<T> {
  data: T | undefined;
  isSuccess: boolean;
  messageError?: string;
  status?: number;
}

export class RequestBuilder {
  private queryClient: QueryClient;
  private baseUrl =
    process.env.REACT_APP_API_BASE_URL || "https://server.convisas.net";

  constructor() {
    this.queryClient = new QueryClient({
      defaultOptions: {
        queries: { staleTime: 0 },
      },
    });
  }

  private generateKey(
    method: HttpMethodEnum,
    url: string,
    params?: any,
  ): string {
    return `${method}:${url}${params ? JSON.stringify(params) : ""}`;
  }

  private buildUrl(url: string, method: HttpMethodEnum, params?: any): string {
    if (method === HttpMethodEnum.GET && params) {
      const queryString = new URLSearchParams(params).toString();
      return `${this.baseUrl}${url}?${queryString}`;
    }
    return `${this.baseUrl}${url}`;
  }

  private async request<T>(
    method: HttpMethodEnum,
    url: string,
    params?: any,
  ): Promise<ApiResponse<T>> {
    const requestUrl = this.buildUrl(url, method, params);
    const authToken = authManager.getAuthToken();
    const headers: Record<string, string> = {};
    if (method !== HttpMethodEnum.GET && params !== undefined) {
      headers["Content-Type"] = "application/json";
    }
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }
    const options: RequestInit = {
      method,
      headers,
      body: method !== HttpMethodEnum.GET ? JSON.stringify(params) : undefined,
    };
    try {
      const response = await fetch(requestUrl, options);
      const rawResponse = await response.text();
      const responseData = rawResponse ? JSON.parse(rawResponse) : {};
      if (!response.ok) {
        return {
          data: undefined,
          isSuccess: false,
          status: response.status,
          messageError:
            responseData?.message ??
            responseData?.data ??
            `Error ${response.status}: ${response.statusText}`,
        };
      }
      return {
        data: responseData.data,
        isSuccess: true,
        status: response.status,
      };
    } catch (error: any) {
      console.error("Fetch error:", error);
      return {
        data: undefined,
        isSuccess: false,
        status: 0,
        messageError: error.message || "Unknown error",
      };
    }
  }

  async requestWithCache<T>(
    method: HttpMethodEnum,
    url: string,
    params?: any,
    staleTime = 0,
  ): Promise<ApiResponse<T>> {
    if (method !== HttpMethodEnum.GET) {
      return this.request<T>(method, url, params);
    }

    const queryKey = [this.generateKey(method, url, params)];
    return this.queryClient.fetchQuery<ApiResponse<T>>({
      queryKey,
      queryFn: () => this.request<T>(method, url, params),
      staleTime,
    });
  }

  async requestHard<T>(
    method: HttpMethodEnum,
    url: string,
    params?: any,
  ): Promise<ApiResponse<T>> {
    const queryKey = this.generateKey(method, url, params);
    await this.queryClient.invalidateQueries({
      queryKey: [queryKey],
      refetchType: "active",
    });
    return this.request<T>(method, url, params);
  }

  createEndpoint<T>(
    method: HttpMethodEnum,
    url: string,
  ): {
    request: (params?: any, staleTime?: number) => Promise<ApiResponse<T>>;
    requestHard: (params?: any) => Promise<ApiResponse<T>>;
  } {
    return {
      request: (params?: any, staleTime?: number): Promise<ApiResponse<T>> =>
        this.requestWithCache<T>(method, url, params, staleTime),
      requestHard: (params?: any): Promise<ApiResponse<T>> =>
        this.requestHard<T>(method, url, params),
    };
  }
}
